import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { QRCodeSVG } from 'qrcode.react';
import Button from './figma/Button';
import Header from './Header';

function Dashboard({ user, reseller, onLogout }) {
  const [earnings, setEarnings] = useState({ total_earnings_dollars: 0, transaction_count: 0 });
  const [customers, setCustomers] = useState([]);
  const [referralLink, setReferralLink] = useState('');
  const [showQRModal, setShowQRModal] = useState(false);

  useEffect(() => {
    if (reseller?.reseller_id) {
      loadDashboardData();
    }
  }, [reseller]);

  const loadDashboardData = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      const apiUrl = process.env.REACT_APP_API_URL || '';
      
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Load earnings
      const earningsResponse = await fetch(`${apiUrl}/earnings`, { headers });
      if (earningsResponse.ok) {
        const earningsData = await earningsResponse.json();
        setEarnings(earningsData);
      }

      // Load customers
      const customersResponse = await fetch(`${apiUrl}/customers`, { headers });
      if (customersResponse.ok) {
        const customersData = await customersResponse.json();
        setCustomers(customersData);
      }

      // Generate referral link
      setReferralLink(`https://remote-assistant.ai/signup?ref=${reseller.reseller_id}`);
    } catch (error) {
      console.error('Dashboard API error:', error);
    }
  };

  // Calculate projected monthly commission
  const projectedMonthly = customers.reduce((total, customer) => {
    const estimatedInvoice = (customer.monthly_payment || 0) + (customer.projected_overage_cost || 0);
    return total + estimatedInvoice;
  }, 0) * ((reseller?.revenue_share_percentage || 30) / 100);

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    alert('Referral link copied to clipboard!');
  };

  const openQRModal = () => setShowQRModal(true);
  const closeQRModal = () => setShowQRModal(false);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={user} onLogout={onLogout} />
      
      {/* Page Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-2xl font-bold text-gray-900">Partner Dashboard</h1>
            <p className="text-sm text-gray-500">{reseller?.business_name}</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <span className="text-green-600 font-bold">$</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Total Earnings</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      ${earnings.total_earnings_dollars?.toFixed(2) || '0.00'}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-bold">#</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Customers</dt>
                    <dd className="text-lg font-medium text-gray-900">{customers.length}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-purple-600 font-bold">%</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Commission Rate</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      {reseller?.revenue_share_percentage || 30}%
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                    <span className="text-indigo-600 font-bold">💰</span>
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">Projected Monthly</dt>
                    <dd className="text-lg font-medium text-gray-900">
                      ${projectedMonthly.toFixed(2)}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Referral Link */}
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Your Referral Link</h3>
            <div className="mt-2 max-w-xl text-sm text-gray-500">
              <p>Share this link with potential customers to earn commissions on their subscriptions.</p>
            </div>
            <div className="mt-5 flex rounded-md shadow-sm">
              <input
                type="text"
                readOnly
                value={referralLink}
                className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-l-md border border-gray-300 bg-gray-50"
              />
              <Button
                onClick={copyReferralLink}
                variant="secondary"
                className="rounded-none border-l-0"
              >
                Copy
              </Button>
              <Button
                onClick={openQRModal}
                variant="secondary"
                className="rounded-l-none border-l-0"
              >
                QR Code
              </Button>
            </div>
          </div>
        </div>

        {/* QR Code Modal */}
        {showQRModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50" onClick={closeQRModal}>
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Scan to Sign Up</h3>
                <div className="flex justify-center mb-6">
                  <QRCodeSVG
                    value={referralLink}
                    size={Math.min(window.innerWidth - 100, 320)}
                    level="H"
                    imageSettings={{
                      src: "/favicon.svg",
                      height: 48,
                      width: 48,
                      excavate: true,
                    }}
                  />
                </div>
                <p className="text-sm text-gray-500 mb-6">Share this QR code with potential customers</p>
                <Button onClick={closeQRModal} variant="primary" className="w-full">
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Customers Table */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Your Customers</h3>
            {customers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Plan
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lifetime
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Minutes
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estimated Usage
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        30 Day Projection
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rev Share
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {customers.map((customer, index) => {
                      const minutes = Math.round(customer.total_assistant_minutes || 0);
                      const included = customer.plan_minutes_included || 0;
                      const percentage = included > 0 ? ((minutes / included) * 100).toFixed(1) : 0;
                      const isOverage = minutes > included;
                      
                      const overageMin = Math.round(customer.overage_minutes || 0);
                      const overageRate = customer.overage_rate || 0;
                      const overageTotal = customer.mtd_overage_total || 0;
                      
                      const projectedUsage = Math.round(customer.projected_usage || 0);
                      const projectedPercentage = included > 0 ? ((projectedUsage / included) * 100).toFixed(1) : 0;
                      
                      const monthlyPayment = customer.monthly_payment || 0;
                      const projectedOverageCost = customer.projected_overage_cost || 0;
                      const estimatedInvoice = monthlyPayment + projectedOverageCost;
                      const revenueShare = estimatedInvoice * ((reseller?.revenue_share_percentage || 30) / 100);
                      const lifetimeEarnings = customer.lifetime_earnings || 0;
                      
                      return (
                        <tr key={index}>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            <div>{customer.customer_name || 'N/A'}</div>
                            <div className="text-xs text-gray-500">{customer.customer_email || ''}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            {customer.subscription_plan || 'N/A'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            ${lifetimeEarnings.toFixed(2)}
                          </td>
                          <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isOverage ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                            <div>{minutes}/{included}</div>
                            <div className="text-xs">{percentage}%</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-blue-600 text-center">
                            <div>{projectedUsage}/{included}</div>
                            <div className="text-xs">{projectedPercentage}%</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="text-xs text-gray-500">
                              ${estimatedInvoice.toFixed(2)} × {reseller?.revenue_share_percentage || 30}%
                            </div>
                            <div className="font-bold">${revenueShare.toFixed(2)}</div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-center">
                            {customer.revenue_share_configured ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                ✓ Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                ✗ Missing
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No customers yet. Share your referral link to get started!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;